import NfcIcon from "@mui/icons-material/Nfc";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNfcScan } from "../hooks/useNfcScan";
import { useQrScan } from "../hooks/useQrScan";
import { TagRead, tagFromText } from "../utils/readTag";

interface Props {
  onRead: (read: TagRead) => void;
}

/** First screen: hold a tag to the phone, scan a QR code, or type the tag. */
export function ScanStep({ onRead }: Props) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const nfc = useNfcScan();
  const qr = useQrScan(videoRef);
  const [typed, setTyped] = useState("");
  const typedTag = tagFromText(typed);

  useEffect(() => {
    if (nfc.last) {
      onRead(nfc.last);
      nfc.clear();
    }
  }, [nfc, onRead]);

  useEffect(() => {
    if (qr.last) {
      onRead(qr.last);
      qr.clear();
    }
  }, [qr, onRead]);

  return (
    <Stack spacing={2}>
      <Button
        variant={nfc.scanning ? "outlined" : "contained"}
        size="large"
        startIcon={<NfcIcon />}
        disabled={!nfc.supported}
        onClick={() => void (nfc.scanning ? nfc.stop() : nfc.start())}
        sx={{ py: 2 }}
      >
        {nfc.scanning ? t("assetScan.scan.nfcWaiting") : t("assetScan.scan.nfc")}
      </Button>
      {!nfc.supported && <Typography variant="caption" color="text.secondary">{t("assetScan.scan.nfcUnsupported")}</Typography>}
      {nfc.error && <Alert severity="warning">{t("assetScan.scan.nfcError", { reason: nfc.error })}</Alert>}

      <Button
        variant="contained"
        size="large"
        startIcon={<QrCodeScannerIcon />}
        disabled={!qr.supported}
        onClick={() => void (qr.scanning ? qr.stop() : qr.start())}
        sx={{ py: 2 }}
      >
        {qr.scanning ? t("assetScan.scan.qrStop") : t("assetScan.scan.qr")}
      </Button>
      {!qr.supported && <Typography variant="caption" color="text.secondary">{t("assetScan.scan.qrUnsupported")}</Typography>}
      {qr.error && <Alert severity="warning">{t("assetScan.scan.cameraError", { reason: qr.error })}</Alert>}
      <Box
        component="video"
        ref={videoRef}
        muted
        playsInline
        sx={{ width: "100%", borderRadius: 1, bgcolor: "black", display: qr.scanning ? "block" : "none" }}
      />

      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          label={t("assetScan.scan.typeTag")}
          placeholder="FF-A:00042"
          value={typed}
          onChange={event => setTyped(event.target.value)}
          error={typed.trim() !== "" && !typedTag}
          helperText={typed.trim() !== "" && !typedTag ? t("assetScan.scan.invalidTag") : " "}
          onKeyDown={event => {
            if (event.key === "Enter" && typedTag) onRead({ tag: typedTag, via: "manual" });
          }}
        />
        <Button variant="outlined" disabled={!typedTag} onClick={() => typedTag && onRead({ tag: typedTag, via: "manual" })} sx={{ height: 40 }}>
          {t("assetScan.scan.find")}
        </Button>
      </Stack>
    </Stack>
  );
}
